// Agent Mailbox DTOs — mirror of riskninja-backend/src/models/agentMailbox.ts
// (AGENT_MAILBOX_PLAN.md §4 "Shared type file").

export type AutonomyLevel = 'shadow' | 'assisted' | 'full';
export type ToolPolicy = 'auto' | 'approval' | 'never';
export type PolicyDecision = 'read' | 'auto' | 'approval' | 'shadow' | 'never';

export type AgentRunStatus =
  | 'pending'
  | 'running'
  | 'awaiting_approval'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'cancelled';

export type InboundEmailStatus = 'received' | 'rejected' | 'quarantined' | 'queued' | 'processing' | 'completed' | 'failed' | 'discarded';

export interface InboundAttachmentInfo {
  filename: string;
  contentType: string;
  size: number;
  documentId?: string | null;
  skipped?: string | null;
  fromForwarded?: boolean;
}

export interface UserLite {
  id: string;
  firstName?: string;
  lastName?: string;
  email: string;
}

export interface InboundEmail {
  id: string;
  messageId?: string;
  fromAddress: string | null;
  fromName: string | null;
  toAddresses?: string[];
  ccAddresses?: string[];
  subject: string | null;
  receivedAt: string | null;
  sentAt?: string | null;
  status: InboundEmailStatus;
  rejectReason: string | null;
  rejectDetail?: string | null;
  attachmentCount: number;
  attachments?: InboundAttachmentInfo[];
  forwardedOriginal?: { from?: string; subject?: string; date?: string; bodyText?: string; more?: Array<{ from?: string; subject?: string; date?: string; bodyText?: string }> } | null;
  headers?: Record<string, string>;
  verdicts?: Record<string, any>;
  bodyText?: string | null;
  pinnedCustomerId: string | null;
  pinnedIntent: string | null;
  plusTag?: string | null;
  resolvedUserId: string | null;
  companyId?: string | null;
  companyName?: string | null;
  simulated: boolean;
  createdAt: string;
  run?: { id: string; status: AgentRunStatus; customerId: string | null; createdAt: string } | null;
}

export interface AgentRun {
  id: string;
  inboundEmailId: string | null;
  triggerType: 'email' | 'manual' | 'task';
  userId: string;
  companyId: string | null;
  conversationId: string | null;
  intakeSessionId: string | null;
  mode: AutonomyLevel;
  model: string | null;
  status: AgentRunStatus;
  instructions: string | null;
  intent: Record<string, any> | null;
  customerId: string | null;
  projectId: string | null;
  summary: string | null;
  nextSteps: string[];
  replyEmailSentAt: string | null;
  /** Verified same-company colleagues the final summary was copied to. */
  summaryCcAddresses?: string[] | null;
  error: string | null;
  tokenUsage: { tokensIn?: number; tokensOut?: number; modelCalls?: number; model?: string };
  iterationCount: number;
  activeMs: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  // list extras
  email?: { id: string; subject: string | null; fromAddress: string | null } | null;
  artifactCount?: number;
  pendingApprovals?: number;
  owner?: UserLite | null;
  companyName?: string | null;
  ownerEmail?: string | null;
  subject?: string | null;
}

export type AgentStepKind =
  | 'model_turn'
  | 'tool_call'
  | 'tool_result'
  | 'decision'
  | 'approval_requested'
  | 'approval_resolved'
  | 'artifact'
  | 'notification'
  | 'error'
  | 'policy_block'
  | 'context';

export interface AgentRunStep {
  id: string;
  runId: string;
  seq: number;
  kind: AgentStepKind;
  tool: string | null;
  args: Record<string, any> | null;
  result: Record<string, any> | null;
  resultS3Key: string | null;
  modelText: string | null;
  status: 'ok' | 'failed' | 'blocked' | 'skipped' | 'pending';
  startedAt: string | null;
  durationMs: number | null;
  tokens: { in?: number; out?: number } | null;
  createdAt: string;
}

export type AgentArtifactType = 'report' | 'document' | 'customer' | 'folder' | 'acord_form' | 'filled_template' | 'certificate' | 'email_draft' | 'pdf' | 'workflow_job' | 'writeup';

export interface AgentArtifact {
  id: string;
  runId: string;
  stepId: string | null;
  type: AgentArtifactType;
  refTable: string | null;
  refId: string | null;
  fileId: string | null;
  title: string | null;
  status: 'ready' | 'pending' | 'failed';
  metadata: Record<string, any>;
  customerId: string | null;
  projectId: string | null;
  createdAt: string;
}

export interface AgentApproval {
  id: string;
  runId: string;
  stepId: string | null;
  action: { tool: string; args: Record<string, any>; displayName?: string };
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  requestedAt: string;
  expiresAt: string | null;
  resolvedAt: string | null;
  resolvedBy: string | null;
  resolutionNote: string | null;
  executionResult: Record<string, any> | null;
  run?: { id: string; status: AgentRunStatus; mode: AutonomyLevel; userId: string; email: { subject: string | null; fromAddress: string | null } | null } | null;
}

export interface AgentEmailDraft {
  id: string;
  runId: string;
  toAddresses: string[];
  ccAddresses: string[];
  subject: string | null;
  bodyText: string | null;
  bodyHtml: string | null;
  attachments: Array<{ artifactId?: string; documentId?: string; filename: string }>;
  audience: 'insured' | 'broker' | 'underwriter' | 'internal';
  status: 'draft' | 'approved' | 'sent' | 'discarded';
  sentAt: string | null;
  updatedAt: string;
}

export interface AgentRunDetail {
  run: AgentRun;
  steps: AgentRunStep[];
  artifacts: AgentArtifact[];
  approvals: AgentApproval[];
  drafts: AgentEmailDraft[];
  inboundEmail: InboundEmail | null;
  owner: UserLite | null;
}

export interface CompanyAgentSettings {
  companyId: string;
  enabled: boolean;
  autonomyLevel: AutonomyLevel;
  toolOverrides: Record<string, ToolPolicy>;
  dailyRunCap: number;
  perSenderHourlyCap: number;
  summaryEmailEnabled: boolean;
}

export interface AgentMailbox {
  id: string;
  address: string;
  label: string | null;
  companyId: string | null;
  status: 'active' | 'paused' | 'disabled';
  transport: 'graph' | 'ses' | 'simulate';
  lastPolledAt?: string | null;
  lastPollError?: string | null;
  companyName?: string | null;
}

export const TERMINAL_RUN_STATUSES: AgentRunStatus[] = ['completed', 'completed_with_errors', 'failed', 'cancelled'];
